import React, { useState, useEffect } from "react";

import { ElementSource, Media, Story, User } from "chq";
import { classnames } from "@culturehq/components";

import { useProfile } from "app/context";
import { useIsLoggedIn, useIsMobile } from "app/hooks";
import { getImage, Trans } from "app/utils";

import imagesSizes from "constants/imagesSizes";
import VideoStudioContext from "components/stories/video-studio/contexts/VideoStudioContext";
import PromptIcon from "components/stories/PromptIcon";
import VideoPlayer from "./VideoPlayer";
import { usePostStoryContext } from "../../contexts/PostStoryContext";
import { PostStoryState, StoryQuestion } from "../../types";
import { isMediaVideoType } from "../../utils";
import VideoPreview from "./VideoPreview";

interface StoryVideoProps {
  currentBody: PostStoryState["currentBody"];
}

const StoryVideo: React.FC<StoryVideoProps> = ({ currentBody }) => {
  const isMobile = useIsMobile();
  const isLoggedIn = useIsLoggedIn();
  const { profile } = useProfile();
  const {
    state: {
      currentStory,
      videoDimension,
      videoDuration,
      postingStory,
      selectedMedia,
      selectedPrompt,
      selectedThumbnailUrl,
      userSelectedImage,
      webVideoRecorded,
      pausedVideo
    },
    setState: setPostStoryState,
    cleanedMediaData,
    handleMediaEdited: onMediaEdited
  } = usePostStoryContext();
  const [mediaText, setMediaText] = useState<string>();
  const [selectedThumbnail, setSelectedThumbnail] = useState();

  useEffect(
    () => {
      const getThumbnail = () => {
        if (selectedThumbnailUrl) {
          return selectedThumbnailUrl;
        }

        const image = getImage(imagesSizes.NORMAL, selectedMedia);
        if (image) {
          return image;
        }

        if (isMediaVideoType(selectedMedia as Blob)) {
          return selectedThumbnailUrl;
        }

        return selectedMedia ? URL.createObjectURL(selectedMedia as Blob) : "";
      };

      setSelectedThumbnail(getThumbnail());
    }, [selectedMedia, selectedThumbnailUrl]
  );

  useEffect(
    () => {
      switch (selectedPrompt?.allowedTypes) {
        case "just_images":
          setMediaText("image");
          break;
        case "just_videos":
          setMediaText("video");
          break;
        default:
          setMediaText("media");
          break;
      }
    }, [selectedPrompt]
  );

  const handleMediaEdited = (
    value: ElementSource,
    thumbnail: PostStoryState["thumbnail"]
  ) => onMediaEdited(0, value, thumbnail);

  const handlePauseVideo = (pausedVideo = true) => {
    setPostStoryState({ pausedVideo });
  };

  if (!isMobile && cleanedMediaData.length > 0) {
    return (
      <>
        <VideoPreview
          story={currentStory}
          mediaVersion={{ additionalData: cleanedMediaData[0] }}
          onPauseVideo={handlePauseVideo}
          pausedVideo={pausedVideo}
        />
        <div className="VideoOptionContainer">
          <div className="VideoOptions">
            <VideoStudioContext
              onMediaEdited={handleMediaEdited}
              story={{ media: {} } as Story}
              mediaVersionHistory={[{
                additionalData: cleanedMediaData[0],
                createdAt: "",
                burnSubtitles: false,
                metadata: {},
                status: "",
                thirdPartyRenderId: "",
                updatedAt: "",
                url: "",
                subtitles: []
              }]}
              onPauseVideo={handlePauseVideo}
              pausedVideo={pausedVideo}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="chq-ffd--bg-img-container StoryMedia">
      <div
        className="chq-ffd--bg-img--img StoryMedia-Background"
        style={{
          backgroundImage: userSelectedImage
            ? `url("${selectedThumbnail}")`
            : "none"
        }}
      />
      <label
        className={classnames(
          "chq-ffd chq-ffd--bg-img",
          { "empty-container": !userSelectedImage },
          {
            "image-container":
              !selectedThumbnailUrl && !isMediaVideoType(selectedMedia as Blob)
          }
        )}
      >
        <div className="chq-ffd--im" style={{ overflow: "hidden" }}>
          {selectedThumbnailUrl || isMediaVideoType(selectedMedia as Blob) ? (
            <>
              <VideoPlayer
                currentStory={currentStory}
                onPauseVideo={handlePauseVideo}
                pausedVideo={pausedVideo}
                postingStory={postingStory}
                selectedThumbnailUrl={selectedThumbnailUrl}
                selectedMedia={selectedMedia}
              />

              <div className="VideoOptionContainer">
                {!isMobile && isLoggedIn
                  && (selectedMedia instanceof File || selectedMedia instanceof Blob) && (
                  <div className="VideoOptions">
                    <VideoStudioContext
                      dimensions={videoDimension}
                      disabled={webVideoRecorded && !videoDuration}
                      onMediaEdited={handleMediaEdited}
                      onPauseVideo={handlePauseVideo}
                      story={{
                        id: 0,
                        bodyTags: [],
                        title: "",
                        questionId: 0,
                        createdAt: "",
                        cheersCount: 0,
                        body: currentBody || "",
                        creator: (currentStory?.creator as User) || profile,
                        media: selectedMedia as unknown as Media,
                        question: selectedPrompt as StoryQuestion,
                        thumbnail: selectedThumbnailUrl as string,
                        videoDuration
                      }}
                      mediaVersionHistory={[{
                        additionalData: cleanedMediaData[0],
                        createdAt: "",
                        burnSubtitles: false,
                        metadata: {},
                        status: "",
                        thirdPartyRenderId: "",
                        updatedAt: "",
                        url: "",
                        subtitles: []
                      }]}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div
              className={classnames(userSelectedImage && "user-selected-image")}
            >
              <div className="Empty-Media">
                <PromptIcon allowedTypes={selectedPrompt?.allowedTypes} />
                <Trans>Your {mediaText} will be displayed here</Trans>
              </div>
            </div>
          )}
        </div>
      </label>
    </div>
  );
};

export default StoryVideo;
